import { flushSync } from "react-dom";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { connect } from "react-redux";
import { Link, useHistory, useLocation } from "react-router-dom";
import { Modal, ModalBody, ModalHeader, Button, FormGroup, Label } from "reactstrap";
import Select from "react-select";
import { Toggle } from "rsuite";

//components
import ProductImagePreView from "./ProductImagePreView";
import ModalCloseIcon from "../../common/Components/Icons/ModalCloseIcon";
import DeleteConfirm from "../../common/Components/DeleteConfirm";
import HKPConfirmDialog from "../../common/Components/HKPConfirmDialog";
import { PRODUCT_KPI_MONTHS_KEY, addStockDelta, parseStoredKpiMonths, stockCountDelta } from "./productKpiWindow";
import OrgProfileCompleteModal from "../profile/OrgProfileCompleteModal";
import notifications from "../../common/Components/Notifications/Notification";
import { debounce } from "../../common/commonFiles/debounce";
import { isProductDraftRevisionsEnabled } from "../../uploads/productDraftRevisionsEnabled";
import { publishProductDraft } from "../../uploads/productDraftApi";
import ProductQrBindingPanel from "./ProductQrBindingPanel";
import CreateVariantCarryModal from "./CreateVariantCarryModal";
import CatalogueListModal from "../catalog/CatalogueListModal";
import { getApi } from "../../helpers/auth_helpers";
import { ALL_CATEGORIES_LIMIT, CategoryOption, toCategoryOptions } from "./categoryFilter";
// product list (redesign)
import ProductStatusTabs from "./list/ProductStatusTabs";
import { usePhotoUpdates } from "./photoUpdate/usePhotoUpdates";
import { REASON_CHIPS } from "./photoUpdate/photoStatus";
import "./photoUpdate/photoUpdate.scss";
import ProductSearchBox from "./list/ProductSearchBox";
import ProductFiltersPanel, { FilterSection } from "./list/ProductFiltersPanel";
import CraftWeavePanel from "./list/CraftWeavePanel";
import ProductColumnMenu from "./list/ProductColumnMenu";
import {
  PRODUCT_LIST_COLUMN_STORAGE_KEY,
  TableColumnField,
  parseTableColumns,
  serializeTableColumns,
} from "./list/productTableColumns";
import { ProductActionSheet } from "./list/ProductActions";
import { ItemHandlers, ProductCard, ProductGridCard, ProductTable } from "./list/ProductItems";
import { PlSheet, useProductListLayout } from "./list/ProductListBits";
import ProductListMore from "./list/ProductListMore";
import { applyPendingStock, forgetSettledStock } from "./list/stockCache";
import { PlIcon } from "./list/plIcons";
import { latestDebounce } from "./list/latestDebounce";
import { ProductActionKey, previewPath, priceSavePair } from "./list/productListRow";
import { isDraftStatus } from "./productStockDisplay";
import {
  DEFAULT_PRODUCT_PAGE_SIZE,
  appendRows,
  depthOf,
  patchRows,
  reloadLimit,
  backEntry,
  parseBack,
  listScroller,
  PRODUCT_LIST_BACK_KEY,
  PRODUCT_FILTERS_KEY,
  PRODUCT_LIST_VIEWS,
  ProductListFilters,
  ProductSort,
  SearchScope,
  StatusTab,
  activeFilterChips,
  addedRange,
  applyStatusTab,
  buildProductQuery,
  clearAllFilters,
  hasActiveProductFilters,
  migrateStoredFilters,
  removeFilterChip,
  searchText,
  setSearchScope,
  setSearchText,
  statusTabFor,
  withDerivedDates,
} from "./list/productListFilters";
import "./productList.scss";
//store
import { getWeaveType } from "../../Store/weave-type/actions";
import { getArtforms } from "../../Store/artform/actions";
import { getSubCategorys } from "../../Store/actions";
import { getTypes } from "../../Store/type/actions";
import { getCategory } from "../../Store/category/actions";
import {
  deleteProducts,
  getProducts,
  changeProductStatus,
  productTransfer,
  addNfcTagForProduct,
  undoTransferProduct,
  updateProductInStock,
  updateProductPrices,
} from "../../Store/products/actions";
import { getOrganisation, connectOrg, getAssocciationsOrg } from "../../Store/organisation/actions";
import { isArray } from "lodash";

const FILTER_PANE_KEY = "hkp.productList.filtersPane";

const SECTION_TITLES: Partial<Record<FilterSection, string>> = {
  category: "Category",
  price: "Selling price",
  colour: "Colour",
  added: "Added",
};

export { PRODUCT_LIST_VIEWS };

const TYPING_DELAY = 600;

const SORT_OPTIONS: Array<{ key: string; label: string; sort: ProductSort }> = [
  { key: "newest", label: "Newest first", sort: { name: "", order: "" } },
  { key: "oldest", label: "Oldest first", sort: { name: "createdAt", order: "asc" } },
  { key: "name-asc", label: "Name A–Z", sort: { name: "name", order: "asc" } },
  { key: "name-desc", label: "Name Z–A", sort: { name: "name", order: "desc" } },
  { key: "design-asc", label: "Design no.", sort: { name: "designNum", order: "asc" } },
];

const TABLET_VIEW_KEY = "hkp.productList.tabletView";

const readTabletView = (): "grid" | "list" => {
  try {
    return window.localStorage.getItem(TABLET_VIEW_KEY) === "list" ? "list" : "grid";
  } catch {
    return "grid";
  }
};

/** "drafted" / "published" from Add product (older builds sent the product status). */
const savedAsDraft = (status: unknown) => {
  const value = String(status || "").trim();
  if (!value) return true;
  if (/^published$/i.test(value)) return false;
  return /^drafted$/i.test(value) || isDraftStatus(value);
};

const sortKeyOf = (sort: ProductSort) => {
  if (!sort.order || (sort.name === "createdAt" && sort.order === "desc")) return "newest";
  return SORT_OPTIONS.find((o) => o.sort.name === sort.name && o.sort.order === sort.order)?.key || "custom";
};

const readJson = (key: string) => {
  try {
    return JSON.parse(window.localStorage.getItem(key) || "null");
  } catch {
    return null;
  }
};

/** Saved filters (migrated from the KPI timeline if needed); a ?view= link from the dashboard starts clean. */
const initialFilters = (search: string): { filters: ProductListFilters; fromLink: boolean } => {
  const stored = migrateStoredFilters(readJson(PRODUCT_FILTERS_KEY), parseStoredKpiMonths(window.localStorage.getItem(PRODUCT_KPI_MONTHS_KEY)));
  const linked = new URLSearchParams(search).get("view") || "";
  if (PRODUCT_LIST_VIEWS[linked]) {
    return { filters: withDerivedDates({ ...clearAllFilters(stored), view: linked, inStock: "" }), fromLink: true };
  }
  return { filters: stored, fromLink: false };
};

export const Products = (props: any) => {
  const history = useHistory();
  const location = useLocation<any>();
  const layout = useProductListLayout();
  const draftRevisionsEnabled = isProductDraftRevisionsEnabled();
  const authUser: any = useMemo(() => readJson("authUser") || "", []);

  const initial = useMemo(() => initialFilters(location.search), []); // eslint-disable-line react-hooks/exhaustive-deps
  const [filters, setFilters] = useState<ProductListFilters>(initial.filters);
  // Back from a product brings the list down to that product; opening the list any other way starts at the top.
  const back = useMemo(() => {
    if (initial.fromLink || history.action !== "POP") return null;
    try {
      return parseBack(window.sessionStorage.getItem(PRODUCT_LIST_BACK_KEY));
    } catch {
      return null;
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const backId = useRef<string | null>(back?.id || null);
  const [page, setPage] = useState<number>(back?.depth || 0);
  // Rows load a page at a time as the list scrolls; `page` is the last page loaded.
  const pageSize = DEFAULT_PRODUCT_PAGE_SIZE;
  const [rows, setRows] = useState<any[] | null>(null);
  const [totalCount, setTotalCount] = useState(0);
  const [loadingMore, setLoadingMore] = useState(false);
  const [moreError, setMoreError] = useState(false);
  const [noMore, setNoMore] = useState(false);
  // True from the moment filters, sort or a reload change what the list should show until that
  // fresh read lands: a next page asked for in between would join rows of a different list.
  const stale = useRef(true);
  const rowsCount = useRef(0);
  const [sort, setSort] = useState<ProductSort>(back?.sort ?? { name: "", order: "" });
  const [loading, setLoading] = useState(false);
  const [listError, setListError] = useState("");
  const [priceFiltersEnabled, setPriceFiltersEnabled] = useState(false);
  const [categoryOptions, setCategoryOptions] = useState<CategoryOption[]>([]);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [sheetView, setSheetView] = useState<"filters" | "craft">("filters");
  const [focusSection, setFocusSection] = useState<FilterSection | null>(null);
  // A phone chip ("Category", "Price"…) opens only its own filter; "Filters" opens them all.
  const [onlySection, setOnlySection] = useState<FilterSection | null>(null);
  // Tablet / laptop filter pane: closed until "Filters" is pressed; the choice is remembered.
  const [paneOpen, setPaneOpen] = useState<boolean>(() => {
    try {
      return window.localStorage.getItem(FILTER_PANE_KEY) === "open";
    } catch {
      return false;
    }
  });
  const togglePane = (next: boolean) => {
    setPaneOpen(next);
    try {
      window.localStorage.setItem(FILTER_PANE_KEY, next ? "open" : "closed");
    } catch {
      /* storage blocked: the pane still toggles for this visit */
    }
  };
  const [sheetProductId, setSheetProductId] = useState<string | null>(null);
  const [countsRefresh, setCountsRefresh] = useState(0);
  const [toast, setToast] = useState<{ id: string; sn: string; name: string; draft: boolean } | null>(null);
  const [tabletView, setTabletView] = useState<"grid" | "list">(readTabletView);

  const [draftToPublish, setDraftToPublish] = useState<any>(null);
  const [publishingDraft, setPublishingDraft] = useState(false);
  const publishingDraftRef = useRef(false);
  const [deleteData] = useState<any>("");
  const [warningModal, setWarningModal] = useState(false);
  const [trans, setTrans] = useState(false);
  const [productName, setProductName] = useState<any>("");
  const [associationData, setAssociationData] = useState<any>({
    associationId: "",
    productOwnerShipTransferFlag: true,
    designOwnerShipTransferFlag: false,
  });
  const [associationOptions, setAssociationOptions] = useState<any[]>([]);
  const associationSearchSeq = useRef(0);
  const [Error, setError]: any = useState({ associationId: "" });
  const [qrProduct, setQrProduct] = useState<any>(null);
  const [openPreview, setOpenPreview] = useState(false);
  const [image, setImage] = useState<any>("");
  const [stockAdjust, setStockAdjust] = useState({ inStock: 0, outOfStock: 0 });
  const stockFlushTimers = useRef<Record<string, ReturnType<typeof setTimeout>>>({});
  const stockRequestSeq = useRef<Record<string, number>>({});
  const confirmedStock = useRef<Record<string, boolean>>({});
  const shownStock = useRef<Record<string, boolean>>({});
  const stockPending = useRef<Record<string, boolean>>({});
  const [columnPickerOpen, setColumnPickerOpen] = useState(false);
  const [tableShown, setTableShown] = useState<TableColumnField[]>(() =>
    parseTableColumns(window.localStorage.getItem(PRODUCT_LIST_COLUMN_STORAGE_KEY))
  );
  const [addProductCatalogueModal, setAddCatalogueProductModal] = useState(false);
  const [variantSource, setVariantSource] = useState<any>(null);
  const [catalogueProduct, setCatalogProduct] = useState(null);

  // ── list loading: always reads the latest filters / page / sort / price flag ──
  const latest = useRef({ filters, page, pageSize, sort, priceFiltersEnabled });
  latest.current = { ...latest.current, priceFiltersEnabled };
  const propsRef = useRef(props);
  propsRef.current = props;
  const loadSeq = useRef(0);
  const loadingRef = useRef(false);
  const rowsRef = useRef<any[] | null>(null);

  const load = useCallback(
    (more = false) => {
      const cur = latest.current;
      if (more && (stale.current || loadingRef.current)) return;
      const query = buildProductQuery(cur.filters, {
        // ceil: a page whose repeat was dropped still counts as loaded. floor would re-ask it and stop the scroll.
        page: more ? Math.ceil(rowsCount.current / cur.pageSize) : 0,
        pageSize: more ? cur.pageSize : reloadLimit(cur.page, cur.pageSize),
        sort: cur.sort,
        priceEnabled: cur.priceFiltersEnabled,
        draftRevisionsEnabled,
      });
      const mine = more ? loadSeq.current : ++loadSeq.current;
      if (more) {
        loadingRef.current = true;
        setLoadingMore(true);
        setMoreError(false);
      } else {
        stale.current = true;
        loadingRef.current = false;
        setLoading(true);
        setLoadingMore(false);
        setMoreError(false);
        setNoMore(false);
      }
      propsRef.current.getProducts(query, (res: any) => {
        if (mine !== loadSeq.current) return;
        if (more) {
          loadingRef.current = false;
          // Filters or sort changed while this page was in flight: it belongs to the old list.
          if (stale.current) {
            setLoadingMore(false);
            return;
          }
        } else stale.current = false;
        setLoading(false);
        setLoadingMore(false);
        const ok = res?.success === true || isArray(res?.data?.docs);
        if (!ok) {
          if (more) {
            setMoreError(true);
            return;
          }
          setListError(
            res?.code === "NETWORK_UNAVAILABLE"
              ? "Couldn’t reach HKP. Check the connection and try again."
              : res?.message || "Products could not be loaded."
          );
          return;
        }
        setListError("");
        const docs: any[] = isArray(res?.data?.docs) ? res.data.docs : [];
        const total = Number(res?.data?.length);
        window.localStorage.setItem("ProductCount", String(Number.isFinite(total) ? total : 0));
        setTotalCount(Number.isFinite(total) ? total : docs.length);
        // Rows from the server are the truth: drop what a past toggle left in the stock caches.
        forgetSettledStock(docs, stockPending.current, shownStock.current, confirmedStock.current);
        const merged = more ? appendRows(rowsRef.current || [], docs) : docs;
        const next = applyPendingStock(merged, stockPending.current, shownStock.current);
        // A page that adds nothing (list shrank, or only repeats) ends the scroll instead of re-asking.
        if (more && (next.length === (rowsRef.current || []).length || docs.length < cur.pageSize)) setNoMore(true);
        rowsRef.current = next;
        rowsCount.current = next.length;
        const depth = depthOf(next.length, cur.pageSize);
        latest.current = { ...latest.current, page: depth };
        setPage(depth);
        setRows(next);
      });
    },
    [draftRevisionsEnabled]
  );
  const loadRef = useRef(load);
  loadRef.current = load;
  const typingLoad = useMemo(() => latestDebounce(loadRef, TYPING_DELAY), []);
  useEffect(() => () => typingLoad.cancel(), [typingLoad]);
  const reload = useCallback(() => {
    typingLoad.cancel();
    stale.current = true;
    loadRef.current();
  }, [typingLoad]);
  const loadMore = useCallback(() => loadRef.current(true), []);
  // The saved entry serves one return only: drop it so a later visit starts at the top. The old pager's
  // "offsetForProduct" page number goes too (read as a scroll depth it once loaded 240 rows at once).
  useEffect(() => {
    try {
      window.sessionStorage.removeItem(PRODUCT_LIST_BACK_KEY);
      window.localStorage.removeItem("offsetForProduct");
    } catch {
      /* ignore */
    }
  }, []);
  // Once the rows are on screen after a Back, bring the product the user opened into view. The route change's
  // own scroll-to-top resets the page at 0, one frame and 50 ms, so the jump runs again after that.
  useEffect(() => {
    const id = backId.current;
    if (!id || !rows) return;
    backId.current = null;
    const show = () => {
      const key = typeof CSS !== "undefined" && CSS.escape ? CSS.escape(id) : id;
      const row = document.querySelector(`[data-pid="${key}"]`);
      if (row) row.scrollIntoView?.({ block: "center", behavior: "instant" as ScrollBehavior });
      // The product left the list (deleted, filtered out): return to where the list was instead.
      else if (back?.scrollTop) {
        const scroller = listScroller();
        if (scroller) scroller.scrollTop = back.scrollTop;
      }
    };
    requestAnimationFrame(show);
    window.setTimeout(show, 120);
  }, [rows]);

  const persistFilters = (next: ProductListFilters) => {
    try {
      window.localStorage.setItem(PRODUCT_FILTERS_KEY, JSON.stringify(next));
    } catch {
      /* storage blocked: filters still apply for this visit */
    }
  };

  /** Apply filters: back to page 1, save, and fetch now (or once typing stops). */
  const commitFilters = useCallback(
    (raw: ProductListFilters, opts: { typing?: boolean } = {}) => {
      const next = withDerivedDates(raw);
      latest.current = { ...latest.current, filters: next, page: 0 };
      stale.current = true;
      window.scrollTo?.({ top: 0 });
      setFilters(next);
      setPage(0);
      persistFilters(next);
      if (opts.typing) typingLoad();
      else reload();
    },
    [reload, typingLoad]
  );

  useEffect(() => {
    if (initial.fromLink) history.replace("/products");
    persistFilters(initial.filters);
    reload();
    props.getCategory({ offset: 0, limit: ALL_CATEGORIES_LIMIT }, (res: any) => {
      setCategoryOptions(toCategoryOptions(res?.data?.docs, true));
    });
    let current = true;
    void getApi(`${process.env.REACT_APP_SERVERURL}/product/price-filter-status`)
      .then((response: any) => {
        if (!current) return;
        const enabled = response?.success === true && response?.data?.enabled === true;
        setPriceFiltersEnabled(enabled);
        const preset = latest.current.filters.price?.preset;
        if (enabled && preset && preset !== "ALL") {
          latest.current = { ...latest.current, priceFiltersEnabled: true };
          reload();
        }
      })
      .catch(() => {
        /* Price filter stays hidden on an older backend or before the price backfill has run. */
      });
    return () => {
      current = false;
      Object.values(stockFlushTimers.current).forEach((timer) => clearTimeout(timer));
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const changeTableColumns = (next: TableColumnField[]) => {
    setTableShown(next);
    try {
      window.localStorage.setItem(PRODUCT_LIST_COLUMN_STORAGE_KEY, serializeTableColumns(next));
    } catch {
      /* storage blocked: the picked columns still apply for this visit */
    }
  };
  const changeTabletView = (next: "grid" | "list") => {
    setTabletView(next);
    try {
      window.localStorage.setItem(TABLET_VIEW_KEY, next);
    } catch {
      /* per-device convenience only */
    }
  };

  // "Saved" toast after Add product (P14): highlight the new piece and offer to open it.
  useEffect(() => {
    const saved = location.state?.savedProduct;
    if (!saved) return undefined;
    setToast({
      id: String(saved.productUniqueId || ""),
      sn: saved.orgSrNo != null && saved.orgSrNo !== "" ? String(saved.orgSrNo) : "",
      name: String(saved.name || ""),
      draft: savedAsDraft(saved.status),
    });
    history.replace({ pathname: location.pathname, search: location.search, state: undefined });
    const timer = setTimeout(() => setToast(null), 6000);
    return () => clearTimeout(timer);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // "/" focuses search on a keyboard.
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement;
      if (event.key !== "/" || event.metaKey || event.ctrlKey) return;
      if (target?.closest?.("input, textarea, select, [contenteditable='true']")) return;
      const input = document.querySelector<HTMLInputElement>(".pl-page .pl-search input");
      if (input) {
        event.preventDefault();
        input.focus();
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, []);

  const changeSort = (next: ProductSort) => {
    latest.current = { ...latest.current, sort: next, page: 0 };
    setSort(next);
    setPage(0);
    reload();
    window.scrollTo?.({ top: 0 });
  };
  const bumpCounts = () => {
    setStockAdjust({ inStock: 0, outOfStock: 0 });
    setCountsRefresh((n) => n + 1);
  };
  const reloadAll = () => {
    reload();
    bumpCounts();
  };
  // A new Added window fetches fresh tab counts; drop the optimistic stock deltas so a toggle
  // is not counted twice (or applied to a window it doesn't belong to).
  const countsWindow = addedRange(filters.added);
  useEffect(() => {
    setStockAdjust({ inStock: 0, outOfStock: 0 });
  }, [countsWindow.createdAfter, countsWindow.createdBefore]);

  // ── actions (unchanged behaviour) ──
  const catalogueModalPopUp = () => setAddCatalogueProductModal(!addProductCatalogueModal);
  const notifyResult = (res: any, onSuccess?: () => void) => {
    if (res?.success) {
      notifications({ title: "Success!", message: res?.message, type: "success" });
      onSuccess?.();
    } else {
      notifications({ title: "Error!", message: res?.message, type: "danger" });
    }
  };
  // HKP tag (NFC) is saved in place from the ⋯ sheet or the laptop tag button; the list reloads to show it.
  const handleTagSave = (product: any, srNo: string, xuid: string) =>
    new Promise<boolean>((resolve) => {
      props.addNfcTagForProduct({ productId: product?._id, srNo, xuid }, (res: any) => {
        notifyResult(res, reload);
        resolve(Boolean(res?.success));
      });
    });
  const openTrans = () => {
    setTrans(true);
    getAssociationOrg();
  };
  const closeTrans = () => {
    setTrans(false);
    setProductName("");
    setError("");
    setAssociationData({ associationId: "", productOwnerShipTransferFlag: true, designOwnerShipTransferFlag: false });
  };
  const deleteDataWithConfirm = (row: any) => {
    props.deleteProducts({ productId: row?._id }, (res: any) => notifyResult(res, reloadAll));
  };
  const handleChangeStatus = (payload: any) => {
    setLoading(true);
    props.changeProductStatus(payload, (res: any) => {
      setLoading(false);
      notifyResult(res, reloadAll);
    });
  };
  const handlePublishProductDraft = async () => {
    const id = String(draftToPublish?._id || "").trim();
    if (!id || publishingDraftRef.current) return;
    publishingDraftRef.current = true;
    setPublishingDraft(true);
    try {
      const res: any = await publishProductDraft(id);
      if (res?.success) {
        notifications({ title: "Success!", message: "Draft published. The live product page has been updated.", type: "success" });
        setDraftToPublish(null);
        reloadAll();
      } else {
        notifications({ title: "Error!", message: "Could not publish this draft. Please review it and try again.", type: "danger" });
      }
    } catch {
      notifications({ title: "Error!", message: "Could not publish this draft. Please try again.", type: "danger" });
    } finally {
      publishingDraftRef.current = false;
      setPublishingDraft(false);
    }
  };
  const undoTransferProductApi = (payload: any) => {
    setLoading(true);
    props.undoTransferProduct(payload, (res: any) => {
      setLoading(false);
      notifyResult(res, reloadAll);
    });
  };
  const [stockNote, setStockNote] = useState<{ product: any; inStock: boolean } | null>(null);
  const stockNoteTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const showStockNote = (note: { product: any; inStock: boolean } | null) => {
    if (stockNoteTimer.current) clearTimeout(stockNoteTimer.current);
    setStockNote(note);
    if (note) stockNoteTimer.current = setTimeout(() => setStockNote(null), 5000);
  };
  useEffect(() => () => {
    if (stockNoteTimer.current) clearTimeout(stockNoteTimer.current);
  }, []);
  /** Changes one shown product in place, so a toggle or price edit shows at once without a reload. */
  const patchRow = (id: string, fields: Record<string, unknown>) => {
    const next = patchRows(rowsRef.current || [], id, fields);
    rowsRef.current = next;
    setRows(next);
  };
  const handleStockToggle = (product: any, inStock: boolean) => {
    const id = product?._id;
    if (!id) return;
    if (isDraftStatus(product?.status)) {
      notifications({
        title: "Still a draft",
        message: "A draft goes in stock when you publish it. Open it and press Publish.",
        type: "info",
      });
      return;
    }
    const displayed = shownStock.current[id] ?? product?.inStock === true;
    if (displayed === inStock) return;
    // A stray tap while scrolling shouldn't quietly mark a piece sold: say what changed, offer Undo.
    showStockNote({ product, inStock });
    if (confirmedStock.current[id] === undefined) confirmedStock.current[id] = displayed;
    shownStock.current[id] = inStock;
    stockPending.current[id] = true;
    patchRow(id, { inStock });
    setStockAdjust((prev) => addStockDelta(prev, stockCountDelta(displayed, inStock)));
    if (stockFlushTimers.current[id]) clearTimeout(stockFlushTimers.current[id]);
    stockFlushTimers.current[id] = setTimeout(() => {
      const seq = (stockRequestSeq.current[id] || 0) + 1;
      stockRequestSeq.current[id] = seq;
      props.updateProductInStock({ id, inStock }, (res: any) => {
        if (stockRequestSeq.current[id] !== seq) return;
        // A newer toggle may still be waiting on its timer: its value is the one in shownStock, so the save
        // that just came back is not the last one and the product stays pending.
        if (shownStock.current[id] === inStock) stockPending.current[id] = false;
        if (res?.success) {
          confirmedStock.current[id] = inStock;
          return;
        }
        const revertTo = confirmedStock.current[id] === true;
        shownStock.current[id] = revertTo;
        patchRow(id, { inStock: revertTo });
        setStockAdjust((prev) => addStockDelta(prev, stockCountDelta(inStock, revertTo)));
        notifications({ title: "Error!", message: res?.message || "Failed to update stock", type: "danger" });
      });
    }, 150);
  };
  const handlePriceSave = (product: any, editedSell: string, editedCost: string) => {
    const id = product?._id;
    if (!id) return;
    const prevSell = String(product?.sellPrice ?? "");
    const prevCost = String(product?.costPrice ?? "");
    if (editedSell === prevSell && editedCost === prevCost) return;
    // With draft revisions, a live piece's price change waits in its draft until publish (owner rule;
    // stock goes live at once). Keep the live price on screen and say so, instead of a silent revert.
    const waitsForPublish =
      product?.priceEditsRequirePublish ?? (draftRevisionsEnabled && !isDraftStatus(product?.status));
    // The other field keeps its pending draft value, so a second edit doesn't undo the first.
    const { sellPrice, costPrice } = waitsForPublish
      ? priceSavePair(product, editedSell, editedCost)
      : { sellPrice: editedSell, costPrice: editedCost };
    if (!waitsForPublish) patchRow(id, { sellPrice, costPrice });
    props.updateProductPrices({ id, sellPrice, costPrice }, (res: any) => {
      if (res?.success) {
        if (waitsForPublish) {
          notifications({
            title: "Saved as a draft change",
            message: "The new price goes live when you publish this piece. Open it and press Publish changes.",
            type: "info",
          });
          reloadAll();
        }
        return;
      }
      if (!waitsForPublish) patchRow(id, { sellPrice: prevSell, costPrice: prevCost });
      notifications({ title: "Error!", message: res?.message || "Failed to update prices", type: "danger" });
    });
  };
  const getAssociationOrg = (search?: any) => {
    const seq = ++associationSearchSeq.current;
    props.getAssocciationsOrg({ offset: 0, limit: 10, search: search?.search || "" }, (res: any) => {
      if (seq !== associationSearchSeq.current) return;
      const docs = isArray(res?.data?.docs) ? res.data.docs : [];
      setAssociationOptions(
        docs.map((ele: any) => {
          const party = ele?.manufacturer?.[0] || {};
          const name = party?.name || "Party";
          const gst = ele?.GST || ele?.gst || "";
          const phone = ele?.phoneNumber || party?.phone || "";
          const detail = [gst, phone].filter(Boolean).join(" · ");
          return { label: detail ? `${name} (${detail})` : name, value: { id: ele?._id, gst } };
        })
      );
    });
  };
  const searchAssociations = useMemo(() => debounce((term: string) => getAssociationOrg({ search: term }), 300), []); // eslint-disable-line react-hooks/exhaustive-deps
  const handleSubmitChange = (type: any) => setAssociationData({ ...associationData, [type?.name]: type?.value });
  const handleOnInputChangAssOrg = (value: string, meta: { action: string }) => {
    if (meta?.action !== "input-change") return;
    const term = value.trim();
    if (term.length >= 2) searchAssociations(term);
    else if (term.length === 0) getAssociationOrg();
  };
  const validate = () => {
    let error = "";
    let err = Error;
    if (associationData?.associationId === "") {
      error = "Required";
      err = { ...err, associationId: " Association Name is Required!" };
    }
    setError(err);
    return error;
  };
  const handleAssSubmit = () => {
    if (validate().length !== 0) return;
    props.productTransfer(
      {
        transferData: [
          {
            id: productName?._id,
            productownershipTransfer: associationData?.productOwnerShipTransferFlag,
            designownershipTransfer: associationData?.designOwnerShipTransferFlag,
          },
        ],
        associationId: associationData?.associationId?.value?.id,
      },
      (res: any) =>
        notifyResult(res, () => {
          reloadAll();
          closeTrans();
        })
    );
  };

  /** Saved just before a product opens, so Back can return the list to it. */
  const rememberOpened = (product: any) => {
    try {
      const entry = backEntry(product?._id, rowsRef.current || [], pageSize, {
        sort: latest.current.sort,
        scrollTop: listScroller()?.scrollTop || 0,
      });
      if (entry) window.sessionStorage.setItem(PRODUCT_LIST_BACK_KEY, JSON.stringify(entry));
    } catch {
      /* storage blocked: Back simply starts at the top */
    }
  };
  const runAction = (key: ProductActionKey, product: any) => {
    const id = product?.productUniqueId;
    if (key === "edit" || key === "view" || key === "preview") rememberOpened(product);
    switch (key) {
      case "publish-draft":
        setDraftToPublish(product);
        break;
      case "edit":
        history.push(`/edit-product/${id}`, { productDetails: product });
        break;
      case "view":
        // Every width opens the in-place view/editor (Figma v4: phone V01, tablet VT01, laptop VL01).
        history.push(`/product/${id}`, { productDetails: product });
        break;
      case "preview":
        history.push(previewPath(product), { productDetails: product });
        break;
      case "tag":
        setSheetProductId(product?._id || null);
        break;
      case "add-qr":
        setQrProduct(product);
        break;
      case "catalogue":
        catalogueModalPopUp();
        setCatalogProduct(id);
        break;
      case "transfer":
        openTrans();
        setProductName(product);
        break;
      case "undo-transfer":
        undoTransferProductApi({ transferData: [{ id: product?._id }], associationId: product?.assosiationId });
        break;
      case "undo-billing":
        history.push(`/sales/take-back?piece=${encodeURIComponent(id || "")}`);
        break;
      case "variant":
        setVariantSource(product);
        break;
      case "archive":
      case "unarchive":
        handleChangeStatus({ productId: product?._id });
        break;
    }
  };

  // ── derived view state ──
  const details = rows;
  const docs: any[] = rows || [];
  const total = totalCount;
  const hasMore = !noMore && docs.length < total;
  const statusTab = statusTabFor(filters);
  const chips = activeFilterChips(filters, {
    priceEnabled: priceFiltersEnabled,
    stockChip: layout === "phone" && statusTab === "inStock",
  });
  const narrowed = hasActiveProductFilters(filters);
  const range = addedRange(filters.added);
  const firstLoad = loading && details == null;
  const sheetProduct = sheetProductId ? docs.find((d) => d?._id === sheetProductId) || null : null;
  const pane = layout !== "phone";

  const itemHandlers: ItemHandlers = {
    draftRevisionsEnabled,
    authUser,
    onOpen: (product) => runAction("view", product),
    onMore: (product, focus) => {
      if (focus !== "price") {
        setSheetProductId(product?._id || null);
        return;
      }
      // "+ Add price": open the sheet inside this tap and focus Selling, so the keyboard comes up.
      flushSync(() => setSheetProductId(product?._id || null));
      (document.querySelector('.pl-action-sheet input[aria-label="Selling price"]') as HTMLInputElement | null)?.focus();
    },
    onStockToggle: handleStockToggle,
    onTagSave: handleTagSave,
    onPreview: (images) => {
      setImage(images);
      setOpenPreview(true);
    },
    onAction: runAction,
    onPriceSave: handlePriceSave,
    highlightId: toast?.id || undefined,
  };

  const loaders = {
    getArtforms: props.getArtforms,
    getTypes: props.getTypes,
    getCategory: props.getCategory,
    getSubCategorys: props.getSubCategorys,
    getWeaveType: props.getWeaveType,
  };

  const onSearchChange = (text: string) => commitFilters(setSearchText(filters, text), { typing: true });
  const onScopeChange = (scope: SearchScope) => {
    const next = setSearchScope(filters, scope);
    if (searchText(next)) {
      commitFilters(next);
      return;
    }
    // Nothing typed yet: remember the scope, no need to fetch.
    latest.current = { ...latest.current, filters: next };
    setFilters(next);
    persistFilters(next);
  };
  const onTab = (tab: StatusTab) => commitFilters(applyStatusTab(filters, tab));
  const photoUpdates = usePhotoUpdates(countsRefresh);
  // A Needs-photo filter saved while the feature was on must not stick once it is off (no tab would show it).
  useEffect(() => {
    if (photoUpdates.known && !photoUpdates.enabled && (filters.needsPhoto || filters.photoReason)) {
      commitFilters({ ...filters, needsPhoto: false, photoReason: "" });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [photoUpdates.known, photoUpdates.enabled, filters.needsPhoto, filters.photoReason]);
  /** Clears search and filters; the status tab above stays (it is not a filter chip). */
  const clearFilters = () => commitFilters(clearAllFilters(filters));
  /** Empty-state escape hatch: everything, including the tab. */
  const showEverything = () => commitFilters({ ...clearAllFilters(filters), view: "", inStock: "" });
  const canClear = chips.length > 0 || Boolean(searchText(filters)) || statusTab !== "all";
  const openFilters = (section: FilterSection | null = null) => {
    setSheetView("filters");
    setFocusSection(section);
    setOnlySection(section);
    setFiltersOpen(true);
  };
  const showLabel = `Show ${total} ${total === 1 ? "product" : "products"}`;

  const sortLabel = SORT_OPTIONS.find((o) => o.key === sortKeyOf(sort))?.label || "Sorted by column";
  const sortSelect = (
    <label className={`pl-sort${layout === "laptop" ? " pl-btn" : ""}`}>
      <PlIcon name="sort" size={layout === "laptop" ? 20 : 16} />
      <span className="pl-sort__text">{layout === "tablet" && sortLabel === "Newest first" ? "Newest" : sortLabel}</span>
      <select
        aria-label="Sort products"
        value={sortKeyOf(sort)}
        onChange={(event) => {
          const option = SORT_OPTIONS.find((o) => o.key === event.target.value);
          if (option) changeSort(option.sort);
        }}
      >
        {SORT_OPTIONS.map((o) => (
          <option key={o.key} value={o.key}>
            {o.label}
          </option>
        ))}
        {sortKeyOf(sort) === "custom" ? <option value="custom">Sorted by column</option> : null}
      </select>
    </label>
  );

  const removableChips = chips.map((chip) => (
    <button
      key={chip.key}
      type="button"
      className="pl-chip is-on"
      aria-label={`Remove filter ${chip.label}`}
      onClick={() => commitFilters(removeFilterChip(filters, chip.key))}
    >
      {chip.label}
      <PlIcon name="x" size={14} />
    </button>
  ));

  const phoneChipRow = (
    <div className="pl-chips is-scroll">
      <button type="button" className="pl-chip is-ink" onClick={() => openFilters()}>
        <PlIcon name="filter" size={16} />
        Filters{chips.length ? ` · ${chips.length}` : ""}
      </button>
      {chips.length ? removableChips : null}
      {/* Quick chips stay for filters not yet used, so a second filter is one tap away. Price comes
          last: it appears once the price-filter check returns and must not shift the others. */}
      {([
        ["category", "Category", "category"],
        ["colour", "Colour", "color"],
        ["added", "Added", "added"],
        ...(priceFiltersEnabled ? [["price", "Price", "price"]] : []),
      ] as Array<[FilterSection, string, string]>)
        .filter(([, , chipKey]) => !chips.some((chip) => chip.key === chipKey))
        .map(([section, label]) => (
          <button key={section} type="button" className="pl-chip" onClick={() => openFilters(section)}>
            {label}
            <PlIcon name="down" size={16} />
          </button>
        ))}
    </div>
  );
  const toastProduct = toast?.id ? docs.find((d) => d?.productUniqueId === toast.id) : null;
  const toastSn = toast ? toast.sn || (toastProduct?.orgSrNo != null && toastProduct.orgSrNo !== "" ? String(toastProduct.orgSrNo) : "") || toast.id : "";
  const toastText = toast
    ? toast.draft
      ? `Saved as draft${toastSn ? ` · #${toastSn} on the loom` : toast.name ? ` · ${toast.name}` : ""}`
      : `Published${toastSn ? ` · #${toastSn}` : toast.name ? ` · ${toast.name}` : ""}`
    : "";
  const countText = details == null || listError ? "" : `${total} ${total === 1 ? "product" : "products"}`;

  let listBody: React.ReactNode;
  if (listError) {
    listBody = (
      <div className="pl-state is-error" role="alert">
        <PlIcon name="warn" size={26} />
        <b>Products could not be loaded</b>
        <span className="pl-meta">{listError}</span>
        <button type="button" className="pl-btn is-primary" onClick={reload}>
          <PlIcon name="retry" />
          Retry
        </button>
      </div>
    );
  } else if (firstLoad) {
    listBody = (
      <div className={`pl-skeletons is-${layout}`} aria-busy="true" aria-label="Loading products">
        {Array.from({ length: layout === "laptop" ? 6 : 4 }).map((_, i) => (
          <div key={i} className="pl-skeleton" />
        ))}
      </div>
    );
  } else if (details != null && docs.length === 0) {
    listBody = narrowed ? (
      <div className="pl-state">
        <b>No products match these filters.</b>
        <button type="button" className="pl-btn is-line" onClick={showEverything}>
          Show all products
        </button>
      </div>
    ) : (
      <div className="pl-state">
        <b>No products yet.</b>
        <span className="pl-meta">Tap Add product to record your first piece.</span>
        <Link className="pl-btn is-primary" to="/createProduct">
          <PlIcon name="plus" />
          Add product
        </Link>
      </div>
    );
  } else if (layout === "laptop") {
    listBody = <ProductTable products={docs} shown={tableShown} sort={sort} onSort={changeSort} h={itemHandlers} busy={loading} />;
  } else if (layout === "tablet" && tabletView === "grid") {
    listBody = (
      <div className={`pl-grid${loading ? " is-refreshing" : ""}`} aria-busy={loading || undefined}>
        {docs.map((product) => (
          <ProductGridCard key={product?._id || product?.productUniqueId} product={product} h={itemHandlers} />
        ))}
      </div>
    );
  } else {
    listBody = (
      <div className={`pl-cards${layout === "tablet" ? " is-two" : ""}${loading ? " is-refreshing" : ""}`} aria-busy={loading || undefined}>
        {docs.map((product) => (
          <ProductCard key={product?._id || product?.productUniqueId} product={product} h={itemHandlers} />
        ))}
      </div>
    );
  }

  return (
    <>
      <HKPConfirmDialog
        open={Boolean(draftToPublish)}
        title="Publish product draft?"
        description={`Publish the saved draft for ${draftToPublish?.name || "this product"}? Its live page will update.`}
        confirmLabel="Publish draft"
        busyLabel="Publishing…"
        busy={publishingDraft}
        onCancel={() => setDraftToPublish(null)}
        onConfirm={handlePublishProductDraft}
      />
      <CatalogueListModal
        {...props}
        openModal={addProductCatalogueModal}
        closeModal={catalogueModalPopUp}
        catalogueProduct={catalogueProduct}
        getProductData={reload}
      />
      {openPreview && <ProductImagePreView {...props} openModal={openPreview} closeModal={() => setOpenPreview(false)} images={image} />}
      <Modal {...props} isOpen={trans} toggle={closeTrans} size="lg" fade={false} className="TransferModal">
        <ModalHeader>
          <div className="Heading">
            <h3>Transfering</h3>
            <ModalCloseIcon {...props} closeModal={closeTrans} />
          </div>
        </ModalHeader>
        <ModalBody>
          <div className="formDiv">
            <Label>
              <b>
                Product Name: <span className="text-capitalise">{productName?.name}</span>
              </b>
            </Label>
          </div>
          <div className="formDiv">
            <Label>
              <b>Association</b>
              <span>*</span>
            </Label>
            <Select
              className="React_selector"
              classNamePrefix="react-select"
              placeholder="Search name, GST, or phone"
              autoFocus={false}
              isSearchable
              filterOption={() => true}
              value={associationData?.associationId}
              options={associationOptions}
              onChange={(e: any) => handleSubmitChange({ name: "associationId", value: e })}
              onInputChange={(value, meta) => handleOnInputChangAssOrg(value, meta)}
            />
            {Error.associationId && !associationData?.associationId ? <p className="RequiredField">{Error?.associationId} </p> : ""}
          </div>
          <div className="formDiv mt-2">
            <Label>
              <b>Product Owner Transfer</b>
            </Label>
            <FormGroup>
              <Toggle
                size="lg"
                checked={
                  associationData?.designOwnerShipTransferFlag == true // eslint-disable-line eqeqeq
                    ? associationData?.designOwnerShipTransferFlag
                    : associationData?.productOwnerShipTransferFlag
                }
                checkedChildren="Yes"
                unCheckedChildren="No"
                onChange={(checked: boolean) => handleSubmitChange({ name: "productOwnerShipTransferFlag", value: checked })}
              />{" "}
            </FormGroup>
          </div>
          <div className="formDiv mt-2">
            <Label>
              <b>Design Owner Transfer</b>
            </Label>
            <FormGroup>
              <Toggle
                size="lg"
                checked={associationData?.designOwnerShipTransferFlag}
                checkedChildren="Yes"
                unCheckedChildren="No"
                onChange={(checked: boolean) => handleSubmitChange({ name: "designOwnerShipTransferFlag", value: checked })}
              />{" "}
            </FormGroup>
          </div>
          <div className="btnS">
            <Button
              disabled={!(associationData?.productOwnerShipTransferFlag === true || associationData?.designOwnerShipTransferFlag === true)}
              onClick={handleAssSubmit}
            >
              Submit
            </Button>
          </div>
        </ModalBody>
      </Modal>
      <CreateVariantCarryModal
        product={variantSource}
        onClose={() => setVariantSource(null)}
        onContinue={(productDetails) => {
          setVariantSource(null);
          history.push(`/createProduct`, { productDetails, name: "variant" });
        }}
      />
      <ProductQrBindingPanel product={qrProduct} open={Boolean(qrProduct)} onClose={() => setQrProduct(null)} onSaved={reload} />
      <DeleteConfirm
        {...props}
        warningModal={warningModal}
        warningToggle={() => setWarningModal(false)}
        deleteDataWithConfirm={deleteDataWithConfirm}
        deleteData={deleteData}
        getAllData={reload}
        name={"Product"}
      />
      <OrgProfileCompleteModal />

      <div className={`pl-page is-${layout}`}>
        <header className="pl-head">
          <div className="pl-head__title">
            <h1>Products</h1>
            <span className="pl-meta">
              {details == null || listError ? "" : narrowed ? `${total} found` : `${total} ${total === 1 ? "piece" : "pieces"}`}
            </span>
          </div>
          <Link className="pl-btn is-primary" to="/createProduct">
            <PlIcon name="plus" />
            {layout === "phone" ? "Add" : "Add product"}
          </Link>
        </header>

        <div className="pl-body">
          {pane && paneOpen ? (
            <aside className="pl-pane" id="pl-filter-pane" aria-label="Filters">
              <div className="pl-pane__inner">
                <div className="pl-pane__head">
                  <span className="pl-grow">Filters</span>
                  <button type="button" className="pl-link" onClick={clearFilters} disabled={!canClear}>
                    Clear all
                  </button>
                  <button type="button" className="pl-iconbtn is-plain" aria-label="Hide filters" onClick={() => togglePane(false)}>
                    <PlIcon name="x" size={18} />
                  </button>
                </div>
                <ProductFiltersPanel
                  mode="pane"
                  filters={filters}
                  onChange={commitFilters}
                  categoryOptions={categoryOptions}
                  priceFiltersEnabled={priceFiltersEnabled}
                  draftRevisionsEnabled={draftRevisionsEnabled}
                  loaders={loaders}
                />
              </div>
            </aside>
          ) : null}

          <section className="pl-main" aria-label="Product list">
            <div className="pl-controls">
              <div className="pl-controls__row">
                {pane ? (
                  <button
                    type="button"
                    className={`pl-btn pl-filtertoggle${paneOpen ? " is-pressed" : ""}`}
                    aria-expanded={paneOpen}
                    aria-controls="pl-filter-pane"
                    onClick={() => togglePane(!paneOpen)}
                  >
                    <PlIcon name="filter" size={18} />
                    Filters
                    {chips.length ? <span className="pl-filtertoggle__n">{chips.length}</span> : null}
                  </button>
                ) : null}
                <ProductSearchBox
                  value={searchText(filters)}
                  scope={filters.searchScope || "all"}
                  onChange={onSearchChange}
                  onScopeChange={onScopeChange}
                  onSubmit={reload}
                  onOpenProduct={(product) => runAction("view", product)}
                  fullScreen={layout === "phone"}
                  keyHint={layout === "laptop"}
                  placeholder={
                    layout === "laptop"
                      ? "Search name, HKP tag, ID, colour… or “red saree in stock”"
                      : layout === "tablet"
                        ? "Search name, HKP tag, ID, colour…"
                        : "Search name, tag, ID, colour…"
                  }
                />
                {layout === "laptop" ? (
                  <>
                    {sortSelect}
                    <ProductColumnMenu shown={tableShown} onChange={changeTableColumns} open={columnPickerOpen} onOpenChange={setColumnPickerOpen} />
                  </>
                ) : null}
              </div>
              <div className="pl-controls__tabs">
                <ProductStatusTabs
                  active={statusTab}
                  onSelect={onTab}
                  showInStock={layout === "laptop"}
                  createdAfter={range.createdAfter}
                  createdBefore={range.createdBefore}
                  stockAdjust={stockAdjust}
                  refreshKey={countsRefresh}
                  needsPhotoCount={photoUpdates.enabled ? photoUpdates.count : null}
                />
                {layout === "tablet" ? (
                  <>
                    {sortSelect}
                    <div className="pl-viewtoggle" role="radiogroup" aria-label="View">
                      {(["grid", "list"] as const).map((view) => (
                        <button
                          key={view}
                          type="button"
                          role="radio"
                          aria-checked={tabletView === view}
                          aria-label={view === "grid" ? "Grid view" : "List view"}
                          className={`pl-iconbtn${tabletView === view ? " is-current" : ""}`}
                          onClick={() => changeTabletView(view)}
                        >
                          <PlIcon name={view} size={18} />
                        </button>
                      ))}
                    </div>
                  </>
                ) : null}
              </div>
              {photoUpdates.enabled && statusTab === "needsPhoto" ? (
                <div className="pu-chips" role="radiogroup" aria-label="Why photos are needed">
                  {REASON_CHIPS.map((chip) => (
                    <button
                      key={chip.key || "all"}
                      type="button"
                      role="radio"
                      aria-checked={(filters.photoReason || "") === chip.key}
                      className={`pu-chip${(filters.photoReason || "") === chip.key ? " is-on" : ""}`}
                      onClick={() => commitFilters({ ...filters, photoReason: chip.key })}
                    >
                      {chip.label}
                    </button>
                  ))}
                </div>
              ) : null}
              {layout === "phone" ? phoneChipRow : null}
            </div>

            <div className="pl-results">
              {layout === "phone" ? (
                <div className="pl-results__bar">
                  <span className="pl-meta pl-b6 pl-grow">{countText}</span>
                  {canClear ? (
                    <button type="button" className="pl-link" onClick={clearFilters}>
                      Clear filters
                    </button>
                  ) : null}
                  {/* The only sort control on phones, so it stays while a search or filter is on. */}
                  {sortSelect}
                </div>
              ) : (
                <div className="pl-results__bar">
                  {chips.length ? <div className="pl-chips is-wrap">{removableChips}</div> : null}
                  <span className="pl-meta pl-b6 pl-grow">{countText ? (chips.length ? `· ${countText}` : countText) : ""}</span>
                </div>
              )}
              {listBody}
              {!listError && docs.length ? (
                <ProductListMore
                  shown={docs.length}
                  total={total}
                  hasMore={hasMore}
                  loading={loadingMore}
                  failed={moreError}
                  onMore={loadMore}
                />
              ) : null}
            </div>
          </section>
        </div>
      </div>

      {layout === "phone" ? (
        <PlSheet
          open={filtersOpen}
          onClose={() => setFiltersOpen(false)}
          label={onlySection ? SECTION_TITLES[onlySection] || "Filters" : "Filters"}
          className={`pl-filter-sheet${onlySection && sheetView === "filters" ? " is-single" : ""}`}
        >
          {(close) =>
            sheetView === "craft" ? (
              <>
                <div className="pl-sheet__bar">
                  <button type="button" className="pl-iconbtn is-plain" aria-label="Back to filters" onClick={() => setSheetView("filters")}>
                    <PlIcon name="back" />
                  </button>
                  <span className="pl-sheet__title">Craft & weave</span>
                  <button type="button" className="pl-link" onClick={() => setSheetView("filters")}>
                    Done
                  </button>
                </div>
                <div className="pl-sheet__scroll">
                  <CraftWeavePanel filters={filters} onChange={(next) => commitFilters(next)} loaders={loaders} />
                </div>
                <div className="pl-sheet__foot">
                  <button type="button" className="pl-btn is-primary is-wide" onClick={() => setSheetView("filters")}>
                    Apply · {total} {total === 1 ? "product" : "products"}
                  </button>
                </div>
              </>
            ) : (
              <>
                <div className="pl-sheet__bar">
                  <span className="pl-sheet__title">{onlySection ? SECTION_TITLES[onlySection] : "Filters"}</span>
                  {onlySection ? (
                    <button type="button" className="pl-link" onClick={() => setOnlySection(null)}>
                      All filters
                    </button>
                  ) : null}
                  {canClear ? (
                    <button type="button" className="pl-link" onClick={clearFilters}>
                      Clear all
                    </button>
                  ) : null}
                  <button type="button" className="pl-iconbtn is-plain" aria-label="Close filters" onClick={close}>
                    <PlIcon name="x" />
                  </button>
                </div>
                <div className="pl-sheet__scroll">
                  <ProductFiltersPanel
                    mode="sheet"
                    filters={filters}
                    onChange={(next, opts) => {
                      commitFilters(next, opts);
                      // A single-choice chip sheet (Category, Colour, Added) works like a dropdown:
                      // picking applies and closes. Typing a colour or picking dates keeps it open.
                      const quickPick =
                        (onlySection === "category" || onlySection === "colour" || onlySection === "added") &&
                        !opts?.typing &&
                        !(onlySection === "added" && next.added?.preset === "custom");
                      if (quickPick) setFiltersOpen(false);
                    }}
                    categoryOptions={categoryOptions}
                    priceFiltersEnabled={priceFiltersEnabled}
                    draftRevisionsEnabled={draftRevisionsEnabled}
                    loaders={loaders}
                    focusSection={focusSection}
                    only={onlySection}
                    onOpenCraft={() => setSheetView("craft")}
                  />
                </div>
                <div className="pl-sheet__foot">
                  <button type="button" className="pl-btn is-primary is-wide" onClick={close} aria-busy={loading || undefined}>
                    {loading ? "Counting…" : showLabel}
                  </button>
                </div>
              </>
            )
          }
        </PlSheet>
      ) : null}

      {sheetProduct ? (
        <ProductActionSheet
          product={sheetProduct}
          authUser={authUser}
          draftRevisionsEnabled={draftRevisionsEnabled}
          onAction={runAction}
          onClose={() => setSheetProductId(null)}
          onPriceSave={handlePriceSave}
          onTagSave={handleTagSave}
          onPreview={itemHandlers.onPreview}
        />
      ) : null}

      {stockNote && !toast ? (
        <div className="pl-toast" role="status">
          <PlIcon name="check" />
          <span className="pl-grow">
            {stockNote.inStock ? "Marked in stock" : "Marked out of stock"}
            {stockNote.product?.orgSrNo != null && stockNote.product.orgSrNo !== "" ? ` · #${stockNote.product.orgSrNo}` : ""}
          </span>
          <button
            type="button"
            className="pl-toast__action"
            onClick={() => {
              const { product, inStock } = stockNote;
              // The live row, not the snapshot taken at the toggle: it carries the value the list shows now.
              // If the row is gone (reloaded away), the snapshot predates the toggle: give it the toggled value.
              const live = (rowsRef.current || []).find((row) => row?._id === product?._id) || { ...product, inStock };
              handleStockToggle(live, !inStock);
              showStockNote(null);
            }}
          >
            Undo
          </button>
        </div>
      ) : null}

      {toast ? (
        <div className="pl-toast" role="status">
          <PlIcon name="check" />
          <span className="pl-grow">{toastText}</span>
          {toast.id ? (
            <button
              type="button"
              className="pl-toast__action"
              onClick={() => {
                const found = docs.find((d) => d?.productUniqueId === toast.id);
                setToast(null);
                if (found && layout !== "laptop") setSheetProductId(found._id);
                else {
                  if (found) rememberOpened(found);
                  history.push(`/product/${toast.id}`, found ? { productDetails: found } : undefined);
                }
              }}
            >
              Open
            </button>
          ) : null}
        </div>
      ) : null}
    </>
  );
};

const mapStateToProps = (state: any) => {
  return {
    ...state,
    productDetails: state?.productReducer?.getProductDetails,
  };
};
export default connect(mapStateToProps, {
  getProducts,
  deleteProducts,
  getCategory,
  getTypes,
  getSubCategorys,
  getArtforms,
  getWeaveType,
  changeProductStatus,
  connectOrg,
  getAssocciationsOrg,
  getOrganisation,
  productTransfer,
  addNfcTagForProduct,
  undoTransferProduct,
  updateProductInStock,
  updateProductPrices,
})(Products);
